import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, ArrowUp, ArrowDown, Save, RefreshCw, Mail, Activity } from "lucide-react";
import { BACKEND_URL } from "../../lib/apiBase";

const API = `${BACKEND_URL}/api`;
const empty = { month: "", monthly: 0, cumulative: 0, paused: false };

// Stable per-row UI key — assigned client-side at load/add so React doesn't
// lose input focus when rows are reordered or filtered. Never sent to backend.
const _uid = () => `tr-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;

const recalcCumulatives = (rs) => {
  let factor = 1;
  return rs.map((r) => {
    const m = Number(r.monthly) || 0;
    factor = factor * (1 + m / 100);
    const cumul = (factor - 1) * 100;
    return { ...r, cumulative: Math.round(cumul * 100) / 100 };
  });
};

export default function TrackRecordEditor() {
  const [rows, setRows] = useState([]);
  const [maxDrawdown, setMaxDrawdown] = useState(-1.42);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingMeta, setSavingMeta] = useState(false);
  const [running, setRunning] = useState(false);
  const navigate = useNavigate();
  const token = typeof window !== "undefined" ? localStorage.getItem("alphalane_admin_token") : null;

  const fetchRows = async () => {
    setLoading(true);
    try {
      const [recRes, metaRes] = await Promise.all([
        axios.get(`${API}/track-record`),
        axios.get(`${API}/track-meta`),
      ]);
      const initial = recRes.data.map((r) => ({ paused: false, ...r, _uikey: _uid() }));
      setRows(recalcCumulatives(initial));
      if (metaRes.data && typeof metaRes.data.max_drawdown === "number") {
        setMaxDrawdown(metaRes.data.max_drawdown);
      }
    } catch {
      toast.error("Konnte Track Record nicht laden.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateRow = (i, patch) =>
    setRows((rs) => recalcCumulatives(rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r))));

  const move = (i, dir) => {
    setRows((rs) => {
      const next = [...rs];
      const j = i + dir;
      if (j < 0 || j >= next.length) return rs;
      [next[i], next[j]] = [next[j], next[i]];
      return recalcCumulatives(next);
    });
  };

  const remove = (i) => setRows((rs) => recalcCumulatives(rs.filter((_, idx) => idx !== i)));
  const addRow = () => setRows((rs) => recalcCumulatives([...rs, { ...empty, month: "Neuer Monat", _uikey: _uid() }]));

  const save = async () => {
    setSaving(true);
    try {
      const cleaned = rows
        .filter((r) => r.month && r.month.trim())
        .map((r) => ({
          month: r.month.trim(),
          monthly: Number(r.monthly) || 0,
          cumulative: Number(r.cumulative) || 0,
          paused: !!r.paused,
        }));
      const res = await axios.post(
        `${API}/admin/track-record`,
        { rows: cleaned },
        { headers: { "x-admin-token": token } }
      );
      setRows(res.data);
      toast.success("Track Record gespeichert. Wird sofort auf der Website angezeigt.");
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("alphalane_admin_token");
        navigate("/admin/login");
      } else {
        toast.error("Speichern fehlgeschlagen.");
      }
    } finally {
      setSaving(false);
    }
  };

  const runDripNow = async () => {
    setRunning(true);
    try {
      const res = await axios.post(`${API}/admin/drip/run`, {}, { headers: { "x-admin-token": token } });
      const { day3, day7, errors } = res.data;
      toast.success(`Drip gelaufen — Day 3: ${day3}, Day 7: ${day7}, Fehler: ${errors}`);
    } catch {
      toast.error("Drip-Job fehlgeschlagen.");
    } finally {
      setRunning(false);
    }
  };

  const saveMeta = async () => {
    setSavingMeta(true);
    try {
      const res = await axios.post(
        `${API}/admin/track-meta`,
        { max_drawdown: Number(maxDrawdown) || 0 },
        { headers: { "x-admin-token": token } }
      );
      setMaxDrawdown(res.data.max_drawdown);
      toast.success("Drawdown gespeichert. Wird sofort auf der Website angezeigt.");
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("alphalane_admin_token");
        navigate("/admin/login");
      } else {
        toast.error("Drawdown konnte nicht gespeichert werden.");
      }
    } finally {
      setSavingMeta(false);
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <span className="al-tag">Editor</span>
          <h1 className="mt-3 font-display font-bold text-3xl sm:text-4xl">
            Track Record <span className="al-grad-text">verwalten.</span>
          </h1>
          <p className="mt-2 text-sm text-slate-600 max-w-xl">
            Hier eingegebene Daten erscheinen sofort auf der Website (Hero + Tabelle) und werden auch in den
            Drip-E-Mails (Tag 7) verwendet — immer tagesaktuell.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={fetchRows} data-testid="tr-reload" className="al-cta-ghost !py-2 !px-3 text-sm">
            <RefreshCw className="h-4 w-4" /> Neu laden
          </button>
          <button onClick={runDripNow} data-testid="tr-run-drip" disabled={running} className="al-cta-ghost !py-2 !px-3 text-sm">
            <Mail className="h-4 w-4" /> {running ? "Läuft..." : "Drip jetzt prüfen"}
          </button>
          <button onClick={save} data-testid="tr-save" disabled={saving} className="al-cta !py-2.5 !px-5 text-sm">
            <Save className="h-4 w-4" /> {saving ? "Speichere..." : "Speichern"}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="al-card p-12 text-center text-slate-500">Lade Daten…</div>
      ) : (
        <>
          <div className="al-card p-6 mb-6 flex flex-wrap items-end gap-5" data-testid="tr-drawdown-editor">
            <div className="flex-1 min-w-[260px]">
              <div className="flex items-center gap-2 text-slate-500 text-[11px] uppercase tracking-widest font-semibold">
                <Activity className="h-3.5 w-3.5" /> Max. Drawdown
              </div>
              <h3 className="mt-2 font-display font-semibold text-lg">Aktueller maximaler Drawdown</h3>
              <p className="mt-1 text-sm text-slate-600">Wird im Track Record als KPI-Box neben „Bester Monat" und „Kumuliert" angezeigt.</p>
            </div>
            <div className="flex items-end gap-3">
              <label className="block">
                <span className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-1.5 block">Wert (in %)</span>
                <input
                  data-testid="tr-drawdown-input"
                  type="number"
                  step="0.01"
                  value={maxDrawdown}
                  onChange={(e) => setMaxDrawdown(e.target.value)}
                  className="al-input-sm w-36 text-right font-mono-tab"
                />
              </label>
              <button
                onClick={saveMeta}
                disabled={savingMeta}
                data-testid="tr-drawdown-save"
                className="al-cta !py-2.5 !px-5 text-sm"
              >
                <Save className="h-4 w-4" /> {savingMeta ? "Speichere..." : "Speichern"}
              </button>
            </div>
          </div>

          <div className="al-card overflow-hidden" data-testid="tr-editor">
            <div className="grid grid-cols-12 gap-3 px-5 py-3 border-b border-[var(--al-line)] bg-[var(--al-bg)] text-[11px] uppercase tracking-widest font-semibold text-slate-500">
              <div className="col-span-4">Monat</div>
              <div className="col-span-2 text-right">Rendite (%)</div>
              <div className="col-span-2 text-right">Kumuliert (%)</div>
              <div className="col-span-2 text-center">Pausiert</div>
              <div className="col-span-2 text-right">Aktion</div>
            </div>

            {rows.map((r, i) => (
              <div key={r._uikey || `tr-row-${i}`} data-testid={`tr-row-${i}`} className="grid grid-cols-12 gap-3 px-5 py-3 items-center border-b border-[var(--al-line)] last:border-b-0">
                <input
                  className="col-span-4 al-input-sm"
                  value={r.month}
                  onChange={(e) => updateRow(i, { month: e.target.value })}
                  placeholder="z.B. Mai 2026"
                />
                <input
                  className="col-span-2 al-input-sm text-right font-mono-tab"
                  type="number"
                  step="0.01"
                  value={r.monthly}
                  disabled={r.paused}
                  onChange={(e) => updateRow(i, { monthly: e.target.value })}
                />
                <input
                  className="col-span-2 al-input-sm text-right font-mono-tab"
                  type="number"
                  step="0.01"
                  value={Number(r.cumulative).toFixed(2)}
                  readOnly
                  title="Wird automatisch aus den monatlichen Renditen berechnet"
                />
                <div className="col-span-2 flex justify-center">
                  <input
                    type="checkbox"
                    checked={!!r.paused}
                    onChange={(e) => updateRow(i, { paused: e.target.checked, monthly: e.target.checked ? 0 : r.monthly })}
                    className="h-5 w-5 accent-[var(--al-purple)]"
                  />
                </div>
                <div className="col-span-2 flex justify-end gap-1">
                  <button onClick={() => move(i, -1)} className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500" title="Hoch">
                    <ArrowUp className="h-4 w-4" />
                  </button>
                  <button onClick={() => move(i, 1)} className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500" title="Runter">
                    <ArrowDown className="h-4 w-4" />
                  </button>
                  <button onClick={() => remove(i)} className="p-1.5 rounded-md hover:bg-rose-50 text-rose-500" title="Löschen">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}

            <div className="px-5 py-4">
              <button onClick={addRow} data-testid="tr-add-row" className="al-cta-ghost text-sm">
                <Plus className="h-4 w-4" /> Monat hinzufügen
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
